import {lazy, FC, Suspense, useMemo} from 'react'
import {Route, Routes, Navigate} from 'react-router-dom'
import {MasterLayout} from '../../_metronic/layout/MasterLayout'
import TopBarProgress from 'react-topbar-progress-indicator'
import {DashboardPage} from '../pages/dashboard/DashboardWrapper'
import {getCSSVariableValue} from '../../_metronic/assets/ts/_utils'
import {DisableSidebar} from '../../_metronic/layout/core'
import {WithChildren} from '../../_metronic/helpers'
import {EditSurveyForm} from '../pages/survey/AddUpdate'
import SurveyResult from '../pages/surveyresult/SurveyResult'
import CourseEvaluationResultsComponent from '../pages/surveyresult/CourseEvaluationResultsComponent'
import SurveyResultGeneral from '../pages/surveyresult/SurveyResultGeneral'
import CreateExam from '../pages/my-course-exam/CreateExam'
import ManualExamBuilder from '../pages/my-course-exam/manual/ManualExamBuilder'
import MeasurementCenterManualExamsPage from '../pages/manual-exam-measurement-center/MeasurementCenterManualExamsPage'
import ExamScheduler from '../pages/examschedule/ExamScheduler'
import ExamScheduleList from '../pages/examschedule/ExamScheduleList'
import ExamSummary from '../pages/examschedule/ExamSummary'
import DepartmentExamStatusPage from '../pages/department-exam-status/DepartmentExamStatusPage'
import ExecutiveReportDashboardPage from '../pages/executive-report-dashboard/Page'
import ProgramOutcomeAchievementPage from '../pages/program-outcome-achievement/Page'
import AssignmentCreator from '../pages/examschedule/AssignmentCreator'
import CourseScheduler from '../pages/courseschedule/CourseScheduler'
import DepartmentCourseScheduler from '../pages/my-course-bolum-schedule/DepartmentCourseScheduler'
import UniversityCourseScheduler from '../pages/university-course-scheduler/UniversityCourseScheduler'
import DepartmentCourseScheduleList from '../pages/my-course-bolum-schedule/DepartmentCourseScheduleList'
import UniversityCourseScheduleList from '../pages/university-course-scheduler/UniversityCourseScheduleList'
import StudentWeeklySchedule from '../pages/my-weekly-schedule/StudentWeeklySchedule'
import InstructorWeeklySchedule from '../pages/my-weekly-schedule/InstructorWeeklySchedule'
import DynamicReportPage from '../pages/dynamicreport/DynamicReport'
import FacultyAvailability from '../pages/examschedule/FacultyAvailability'
import ManagementSwapRequests from '../pages/examschedule/ManagementSwapRequests'
import ManagementMakeupRequests from '../pages/makeup/ManagementMakeupRequests'
import StudentList from '../pages/my-course-exam/StudentList'
import ExamOpticalReadingPage from '../pages/exam-optical-reading/ExamOpticalReadingPage'
import CourseOutcomesReport from '../pages/my-course/CourseOutcomesReport'
import SurveyResultSemester from '../pages/surveyresult/SurveyResultSemester'
import SurveyResultLecturer from '../pages/surveyresult/SurveyResultLecturer'
import OrganizationOutcomeContribution from '../pages/organizationoutcome/OrganizationOutcomeContribution'
import {AutoLogin} from '../modules/auth/components/AutoLogin'
import {useAuth} from '../modules/auth'
import {useModule} from '../platform'
import {ModuleScopedRoutes} from './ModuleScopedRoutes'
import {buildModuleRouteEntitlements} from '../platform/navigation'

const react_app_title = process.env.REACT_APP_TITLE?.toLowerCase()

const PrivateRoutes = () => {
  const {defaultRoute, moduleCode} = useModule()
  const {auth, currentUser} = useAuth()
  const activeRoleName = auth?.role || currentUser?.roleName
  const normalizedRole = (activeRoleName || '').trim().toLocaleUpperCase('tr-TR')
  const isTeacherRole =
    normalizedRole === 'TEACHER' ||
    normalizedRole.includes('ÖĞRETİM ÜYESİ') ||
    normalizedRole.includes('ÖĞRETİM ELEMANI')
  const isStudentRole = normalizedRole === 'STUDENT' || normalizedRole.includes('ÖĞRENCİ')
  const moduleFallbackRoute = moduleCode ? defaultRoute : '/dashboard'
  const routeEntitlements = useMemo(
    () =>
      moduleCode
        ? buildModuleRouteEntitlements({
            defaultRoute,
            menus: currentUser?.menus,
            moduleCode,
            roleName: activeRoleName,
          })
        : [],
    [activeRoleName, currentUser?.menus, defaultRoute, moduleCode]
  )
  const ProfilePage = lazy(() => import('../modules/profile/ProfilePage'))
  const WizardsPage = lazy(() => import('../modules/wizards/WizardsPage'))
  const AccountPage = lazy(() => import('../modules/accounts/AccountPage'))
  const WidgetsPage = lazy(() => import('../modules/widgets/WidgetsPage'))
  const ChatPage = lazy(() => import('../modules/apps/chat/ChatPage'))
  const ModelListPage = lazy(() => import('../pages/modellist/Page'))
  const MethodListPage = lazy(() => import('../pages/methodlist/Page'))
  const RoleMethodListPage = lazy(() => import('../pages/rolemethodlist/Page'))
  const RoleModelListPage = lazy(() => import('../pages/rolemodellist/Page'))
  const MenuListPage = lazy(() => import('../pages/menulist/Page'))
  const MenuRolePage = lazy(() => import('../pages/menurole/Page'))
  const UserActionPage = lazy(() => import('../pages/useraction/Page'))
  const CulturePage = lazy(() => import('../pages/culture/Page'))
  const TranslationPage = lazy(() => import('../pages/translation/Page'))
  const ApplicationUserPage = lazy(() => import('../pages/applicationuser/Page'))
  const ApplicationRolePage = lazy(() => import('../pages/applicationrole/Page'))
  const ApplicationUserRolePage = lazy(() => import('../pages/applicationuserrole/Page'))
  const ApplicationUserClaimPage = lazy(() => import('../pages/applicationuserclaim/Page'))
  const ApplicationRoleClaimPage = lazy(() => import('../pages/applicationroleclaim/Page'))
  const TenantPage = lazy(() => import('../pages/tenant/Page'))
  const AnswerPage = lazy(() => import('../pages/answer/Page'))
  const AnswerQuestionItemPage = lazy(() => import('../pages/answerquestionitem/Page'))
  const CodePage = lazy(() => import('../pages/code/Page'))
  const CoursePage = lazy(() => import('../pages/course/Page'))
  const MyCoursePage = lazy(() => import('../pages/my-course/Page'))
  const MyCourseBPage = lazy(() => import('../pages/my-course-bolum/Page'))
  const MyCourseExamPage = lazy(() => import('../pages/my-course-exam/Page'))
  const StudentEExamPage = lazy(() => import('../pages/e-exam/StudentEExamPage'))
  const MyCourseOutcomePage = lazy(() => import('../pages/my-course-outcome/Page'))
  const CourseOutcomePage = lazy(() => import('../pages/courseoutcome/Page'))
  const ExamPage = lazy(() => import('../pages/exam/Page'))
  const ExamQuestionPage = lazy(() => import('../pages/examquestion/Page'))
  const OrganizationPage = lazy(() => import('../pages/organization/Page'))
  const OrganizationEducationPurposePage = lazy(
    () => import('../pages/organizationeducationpurpose/Page')
  )
  const OrganizationOutcomePage = lazy(() => import('../pages/organizationoutcome/Page'))
  const OutcomePointPage = lazy(() => import('../pages/outcomepoint/Page'))
  const QuestionBankPage = lazy(() => import('../pages/questionbank/Page'))
  const QuestionItemPage = lazy(() => import('../pages/questionitem/Page'))
  const SemesterPage = lazy(() => import('../pages/semester/Page'))
  const SemesterCoursePage = lazy(() => import('../pages/semestercourse/Page'))
  const StudentPage = lazy(() => import('../pages/student/Page'))
  const MyConsultantStudentsPage = lazy(() => import('../pages/my-consultant-students/Page'))
  const StudentConsultantFollowUpPage = lazy(
    () => import('../pages/student-consultant-follow-up/Page')
  )
  const GraduateApplicationsPage = lazy(() => import('../pages/graduate-applications/Page'))
  const StudentClearancePage = lazy(() => import('../pages/student-clearance/Page'))
  const StudentClearanceStudentPage = lazy(
    () => import('../pages/student-clearance/StudentClearanceStudentPage')
  )
  const StudentSemesterCoursePage = lazy(() => import('../pages/studentsemestercourse/Page'))
  const SurveyPage = lazy(() => import('../pages/survey/Page'))
  const SurveyApplicationPage = lazy(() => import('../pages/surveyapplication/Page'))
  const SurveyFrequencyPage = lazy(() => import('../pages/surveyfrequency/Page'))
  const SurveyQuestionPage = lazy(() => import('../pages/surveyquestion/Page'))
  const SurveyQuestionTypePage = lazy(() => import('../pages/surveyquestiontype/Page'))
  const SurveyQuestionTypeAnswerPage = lazy(() => import('../pages/surveyquestiontypeanswer/Page'))
  const SurveyTypePage = lazy(() => import('../pages/surveytype/Page'))
  const CourseOutcomeQuestion = lazy(() => import('../pages/courseoutcomequestion/Page'))
  const LearningObjectiveQuestionPage = lazy(
    () => import('../pages/learningobjectivequestion/Page')
  )
  const ExamType = lazy(() => import('../pages/examtype/Page'))
  const Gender = lazy(() => import('../pages/gender/Page'))
  const ReportPage = lazy(() => import('../pages/report/Page'))
  const ReportItemPage = lazy(() => import('../pages/reportitem/Page'))
  const ReportItemOrganizationPage = lazy(() => import('../pages/reportitemorganization/Page'))
  const EvidenceFilePage = lazy(() => import('../pages/evidencefile/Page'))
  const EvidenceMaturityManagementPage = lazy(() => import('../pages/evidencematurity/Page'))
  const EvidenceMaturityTypePage = lazy(() => import('../pages/evidencematuritytype/Page'))
  const EvidenceMaturityLevelPage = lazy(() => import('../pages/evidencematuritylevel/Page'))
  const Questiontype = lazy(() => import('../pages/questiontype/Page'))
  const SemesterCourseReport = lazy(() => import('../pages/semestercoursereport/Page'))
  const ReportItemOrganizationEvidence = lazy(
    () => import('../pages/reportitemorganizationevidence/Page')
  )
  const MyReport = lazy(() => import('../pages/my-report/Page'))
  const MyReportItem = lazy(() => import('../pages/my-reportitem/Page'))
  const QuestionRequestPage = lazy(() => import('../pages/questionrequest/Page'))
  const SemesterReport = lazy(() => import('../pages/semesterreport/Page'))
  const CourseOrganizationOutcomeMatrix = lazy(
    () => import('../pages/courseorganizationoutcomematrix/Page')
  )
  const BoardModulePage = lazy(() => import('../pages/boardmodule/Page'))
  const BoardModuleObjectivePage = lazy(() => import('../pages/boardmoduleobjective/Page'))
  const MyBoardModulePage = lazy(() => import('../pages/my-boardmodule/Page'))
  const BoardSchedulePage = lazy(() => import('../pages/boardschedule/Page'))
  const BoardTimeSlotSetPage = lazy(() => import('../pages/boardtimeslotset/Page'))
  const TimeSlotPage = lazy(() => import('../pages/timeslot/Page'))
  const Setting = lazy(() => import('../pages/setting/Page'))
  const TeachingAssignmentPage = lazy(() => import('../pages/teachingassignment/Page'))
  const AssignmentRolePage = lazy(() => import('../pages/assignmentrole/Page'))
  const AssignmentRulePage = lazy(() => import('../pages/assignmentrule/Page'))
  const TeachingAssignmentSlotPage = lazy(() => import('../pages/teachingassignmentslot/Page'))
  const BuildingPage = lazy(() => import('../pages/building/Page'))
  const ClassroomPage = lazy(() => import('../pages/classroom/Page'))
  const OrganizationAssignmentPage = lazy(() => import('../pages/organizationassignment/Page'))
  const OrganizationClassroomPage = lazy(() => import('../pages/organizationclassroom/Page'))
  const CourseScheduleClassroomPage = lazy(() => import('../pages/course-schedule-classroom/Page'))
  const FacultyAvailabilityPage = lazy(() => import('../pages/facultyavailability/Page'))
  const ExamSeatAssignmentPage = lazy(() => import('../pages/examseatassignment/Page'))
  const PeriodLoadCounterPage = lazy(() => import('../pages/periodloadcounter/Page'))
  const AdditionalTeachingPage = lazy(() => import('../pages/additional-teaching/Page'))
  const AdditionalTeachingCalculationPage = lazy(
    () => import('../pages/additional-teaching-calculation/Page')
  )
  const ExamPeriodPage = lazy(() => import('../pages/examperiod/Page'))
  const MyExamDutyArchivePage = lazy(() => import('../pages/my-exam-duty-archive/Page'))
  const ExamRulesPage = lazy(() => import('../pages/exam-rules/Page'))
  const AppliedEducationVisitPage = lazy(() => import('../pages/applied-education-visit/Page'))
  const BapProjectApplicationPage = lazy(() => import('../pages/bap-project-application/Page'))
  const BapProjectManagePage = lazy(() => import('../pages/bap-project-manage/Page'))
  const BapProjectDefinitionPage = lazy(() => import('../pages/bap-project-definition/Page'))
  const BapProjectReportsPage = lazy(() => import('../pages/bap-project-reports/Page'))
  const QualityPdcaPage = lazy(() => import('../pages/quality-pdca/Page'))
  const SurveyFeedbackModulePage = lazy(
    () => import('../pages/module-home/SurveyFeedbackModulePage')
  )
  const ModuleDashboardPage = lazy(() => import('../pages/module-dashboard/Page'))
  const PersonalExamSchedulePage = lazy(() => import('../pages/personal-exam-schedule/Page'))
  const GraduateCardModulePage = lazy(() => import('../pages/module-home/GraduateCardModulePage'))
  const CommitteePage = lazy(() => import('../pages/committee/Page'))

  if (react_app_title === 'student-clearance') {
    return (
      <Routes>
        <Route element={<MasterLayout />}>
          <Route path='auth/autologin/:token' element={<AutoLogin />} />
          <Route path='auth/*' element={<Navigate to='/' />} />
          <Route index element={<Navigate to='/student-clearance-request' replace />} />
          <Route path='dashboard' element={<Navigate to='/student-clearance-request' replace />} />
          <Route
            path='student-clearance-request'
            element={
              <SuspensedView>
                <StudentClearanceStudentPage />
              </SuspensedView>
            }
          />
          <Route
            path='graduate-card'
            element={
              <SuspensedView>
                <GraduateCardModulePage />
              </SuspensedView>
            }
          />
          <Route path='*' element={<Navigate to='/student-clearance-request' replace />} />
        </Route>
      </Routes>
    )
  }

  return (
    <ModuleScopedRoutes moduleCode={moduleCode} routeEntitlements={routeEntitlements}>
      <Route element={<MasterLayout />}>
        <Route path='auth/autologin/:token' element={<AutoLogin />} />
        <Route path='auth/*' element={<Navigate to='/' />} />

        <Route path='dashboard' element={<DashboardPage />} />
        <Route
          path='module-dashboard/*'
          element={
            moduleCode ? (
              <SuspensedView>
                <ModuleDashboardPage />
              </SuspensedView>
            ) : (
              <Navigate to='/dashboard' replace />
            )
          }
        />
        <Route path='/executive-report-dashboard' element={<ExecutiveReportDashboardPage />} />
        <Route
          path='/quality-pdca'
          element={
            <SuspensedView>
              <QualityPdcaPage />
            </SuspensedView>
          }
        />
        <Route
          path='/survey-feedback'
          element={
            <SuspensedView>
              <SurveyFeedbackModulePage />
            </SuspensedView>
          }
        />
        <Route
          path='/graduate-card'
          element={
            <SuspensedView>
              <GraduateCardModulePage />
            </SuspensedView>
          }
        />
        <Route
          path='graduate-applications/*'
          element={
            <SuspensedView>
              <GraduateApplicationsPage />
            </SuspensedView>
          }
        />
        <Route
          path='student-clearance'
          element={
            react_app_title === 'dsos' ? (
              <Navigate to='/dashboard' replace />
            ) : (
              <SuspensedView>
                <StudentClearancePage />
              </SuspensedView>
            )
          }
        />
        <Route
          path='student-clearance-request'
          element={
            <SuspensedView>
              <StudentClearanceStudentPage />
            </SuspensedView>
          }
        />
        <Route path='/addupdatesurvey' element={<EditSurveyForm />} />
        <Route path='/surveyresult' element={<SurveyResult />} />
        <Route path='/surveyresultmanagement' element={<CourseEvaluationResultsComponent />} />
        <Route path='/surveyresultgeneral' element={<SurveyResultGeneral />} />
        <Route path='/createexam' element={<CreateExam />} />
        <Route path='/my-course-exam/manual' element={<ManualExamBuilder />} />
        <Route
          path='/manual-exams/measurement-center'
          element={<MeasurementCenterManualExamsPage />}
        />
        <Route
          path='/e-exam/:examSeatAssignmentId'
          element={
            !moduleCode ||
            moduleCode === 'assessment' ||
            (moduleCode === 'course-exam-schedule' && isStudentRole) ? (
              <SuspensedView>
                <StudentEExamPage />
              </SuspensedView>
            ) : (
              <Navigate to={moduleFallbackRoute} replace />
            )
          }
        />
        <Route path='/studentlist' element={<StudentList />} />
        <Route path='/exam-optical-reading' element={<ExamOpticalReadingPage />} />
        <Route
          path='/my-exam-schedule'
          element={
            moduleCode === 'course-exam-schedule' &&
            (isTeacherRole || isStudentRole) ? (
              <SuspensedView>
                <PersonalExamSchedulePage />
              </SuspensedView>
            ) : (
              <Navigate to={moduleFallbackRoute} replace />
            )
          }
        />
        <Route path='/courseoutcomesreport' element={<CourseOutcomesReport />} />
        <Route
          path='/question-requests/incoming'
          element={
            (moduleCode && (moduleCode !== 'assessment' || !isTeacherRole)) ||
            react_app_title === 'dsos' ? (
              <Navigate to={moduleFallbackRoute} replace />
            ) : (
              <SuspensedView>
                <QuestionRequestPage />
              </SuspensedView>
            )
          }
        />
        <Route
          path='/evidence-maturity-management'
          element={
            <SuspensedView>
              <EvidenceMaturityManagementPage />
            </SuspensedView>
          }
        />
        <Route
          path='/evidence-maturity-type/*'
          element={
            <SuspensedView>
              <EvidenceMaturityTypePage />
            </SuspensedView>
          }
        />
        <Route
          path='/evidence-maturity-level/*'
          element={
            <SuspensedView>
              <EvidenceMaturityLevelPage />
            </SuspensedView>
          }
        />
        <Route path='/surveyresultsemester' element={<SurveyResultSemester />} />
        <Route path='/surveyresultlecturer' element={<SurveyResultLecturer />} />
        <Route
          path='/my-course-outcome'
          element={
            <SuspensedView>
              <MyCourseOutcomePage />
            </SuspensedView>
          }
        />
        <Route
          path='/organizationoutcomescontribution'
          element={<OrganizationOutcomeContribution />}
        />

        {/* Lazy Modules */}
        <Route
          path='crafted/pages/profile/*'
          element={
            <SuspensedView>
              <ProfilePage />
            </SuspensedView>
          }
        />
        <Route
          path='crafted/pages/wizards/*'
          element={
            <SuspensedView>
              <WizardsPage />
            </SuspensedView>
          }
        />
        <Route
          path='exam-scheduler'
          element={
            <SuspensedView>
              <ExamScheduler />
            </SuspensedView>
          }
        />
        <Route
          path='course-scheduler'
          element={
            <SuspensedView>
              <CourseScheduler />
            </SuspensedView>
          }
        />
        <Route
          path='department-course-scheduler'
          element={
            <SuspensedView>
              <DepartmentCourseScheduler />
            </SuspensedView>
          }
        />
        <Route
          path='department-course-schedule'
          element={
            <SuspensedView>
              <DepartmentCourseScheduleList />
            </SuspensedView>
          }
        />
        <Route
          path='university-course-scheduler'
          element={
            <SuspensedView>
              <UniversityCourseScheduler />
            </SuspensedView>
          }
        />
        <Route
          path='university-course-schedule'
          element={
            <SuspensedView>
              <UniversityCourseScheduleList />
            </SuspensedView>
          }
        />
        <Route
          path='my-student-schedule'
          element={
            <SuspensedView>
              <StudentWeeklySchedule />
            </SuspensedView>
          }
        />
        <Route
          path='my-instructor-schedule'
          element={
            <SuspensedView>
              <InstructorWeeklySchedule />
            </SuspensedView>
          }
        />
        <Route
          path='my-exam-duty-archive'
          element={
            <SuspensedView>
              <MyExamDutyArchivePage />
            </SuspensedView>
          }
        />
        <Route
          path='exam-summary'
          element={
            <SuspensedView>
              <ExamSummary />
            </SuspensedView>
          }
        />
        <Route
          path='department-exam-status'
          element={
            <SuspensedView>
              <DepartmentExamStatusPage />
            </SuspensedView>
          }
        />
        <Route
          path='program-outcome-achievement'
          element={
            <SuspensedView>
              <ProgramOutcomeAchievementPage />
            </SuspensedView>
          }
        />
        <Route
          path='dynamic-report'
          element={
            <SuspensedView>
              <DynamicReportPage />
            </SuspensedView>
          }
        />
        <Route
          path='exam-schedule-list'
          element={
            <SuspensedView>
              <ExamScheduleList />
            </SuspensedView>
          }
        />
        <Route
          path='assignment-creator'
          element={
            <SuspensedView>
              <AssignmentCreator />
            </SuspensedView>
          }
        />
        <Route
          path='faculty-availability'
          element={
            <SuspensedView>
              <FacultyAvailability />
            </SuspensedView>
          }
        />
        <Route
          path='manage-swap-requests'
          element={
            <SuspensedView>
              <ManagementSwapRequests />
            </SuspensedView>
          }
        />
        <Route
          path='manage-makeup-requests'
          element={
            <SuspensedView>
              <ManagementMakeupRequests />
            </SuspensedView>
          }
        />
        <Route
          path='crafted/widgets/*'
          element={
            <SuspensedView>
              <WidgetsPage />
            </SuspensedView>
          }
        />
        <Route
          path='my-report/*'
          element={
            <SuspensedView>
              <MyReport />
            </SuspensedView>
          }
        />
        <Route
          path='my-reportitem/*'
          element={
            <SuspensedView>
              <MyReportItem />
            </SuspensedView>
          }
        />
        <Route
          path='crafted/account/*'
          element={
            <SuspensedView>
              <AccountPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/chat/*'
          element={
            <SuspensedView>
              <ChatPage />
            </SuspensedView>
          }
        />
        <Route
          path='courseoutcomequestion/*'
          element={
            <SuspensedView>
              <CourseOutcomeQuestion />
            </SuspensedView>
          }
        />
        <Route
          path='learningobjectivequestion/*'
          element={
            <SuspensedView>
              <LearningObjectiveQuestionPage />
            </SuspensedView>
          }
        />
        <Route
          path='examtype/*'
          element={
            <SuspensedView>
              <ExamType />
            </SuspensedView>
          }
        />
        <Route
          path='gender/*'
          element={
            <SuspensedView>
              <Gender />
            </SuspensedView>
          }
        />
        <Route
          path='examperiod/*'
          element={
            <SuspensedView>
              <ExamPeriodPage />
            </SuspensedView>
          }
        />
        <Route
          path='exam-rules/*'
          element={
            <SuspensedView>
              <ExamRulesPage />
            </SuspensedView>
          }
        />
        <Route
          path='timeslot/*'
          element={
            <SuspensedView>
              <TimeSlotPage />
            </SuspensedView>
          }
        />
        <Route
          path='reportitemorganizationevidence/*'
          element={
            <SuspensedView>
              <ReportItemOrganizationEvidence />
            </SuspensedView>
          }
        />
        <Route
          path='semestercoursereport/*'
          element={
            <SuspensedView>
              <SemesterCourseReport />
            </SuspensedView>
          }
        />

        <Route
          path='questiontype/*'
          element={
            <SuspensedView>
              <Questiontype />
            </SuspensedView>
          }
        />
        <Route
          path='modellist/*'
          element={
            <SuspensedView>
              <ModelListPage />
            </SuspensedView>
          }
        />
        <Route
          path='methodlist/*'
          element={
            <SuspensedView>
              <MethodListPage />
            </SuspensedView>
          }
        />
        <Route
          path='rolemethodlist/*'
          element={
            <SuspensedView>
              <RoleMethodListPage />
            </SuspensedView>
          }
        />
        <Route
          path='rolemodellist/*'
          element={
            <SuspensedView>
              <RoleModelListPage />
            </SuspensedView>
          }
        />
        <Route
          path='menulist/*'
          element={
            <SuspensedView>
              <MenuListPage />
            </SuspensedView>
          }
        />
        <Route
          path='menurole/*'
          element={
            <SuspensedView>
              <MenuRolePage />
            </SuspensedView>
          }
        />
        <Route
          path='useraction/*'
          element={
            <SuspensedView>
              <UserActionPage />
            </SuspensedView>
          }
        />
        <Route
          path='culture/*'
          element={
            <SuspensedView>
              <CulturePage />
            </SuspensedView>
          }
        />
        <Route
          path='translation/*'
          element={
            <SuspensedView>
              <TranslationPage />
            </SuspensedView>
          }
        />
        <Route
          path='applicationuser/*'
          element={
            <SuspensedView>
              <ApplicationUserPage />
            </SuspensedView>
          }
        />
        <Route
          path='applicationrole/*'
          element={
            <SuspensedView>
              <ApplicationRolePage />
            </SuspensedView>
          }
        />
        <Route
          path='applicationuserrole/*'
          element={
            <SuspensedView>
              <ApplicationUserRolePage />
            </SuspensedView>
          }
        />
        <Route
          path='applicationuserclaim/*'
          element={
            <SuspensedView>
              <ApplicationUserClaimPage />
            </SuspensedView>
          }
        />
        <Route
          path='applicationroleclaim/*'
          element={
            <SuspensedView>
              <ApplicationRoleClaimPage />
            </SuspensedView>
          }
        />
        <Route
          path='tenant/*'
          element={
            <SuspensedView>
              <TenantPage />
            </SuspensedView>
          }
        />
        <Route
          path='answer/*'
          element={
            <SuspensedView>
              <AnswerPage />
            </SuspensedView>
          }
        />
        <Route
          path='answerquestionitem/*'
          element={
            <SuspensedView>
              <AnswerQuestionItemPage />
            </SuspensedView>
          }
        />
        <Route
          path='code/*'
          element={
            <SuspensedView>
              <CodePage />
            </SuspensedView>
          }
        />
        <Route
          path='course/*'
          element={
            <SuspensedView>
              <CoursePage />
            </SuspensedView>
          }
        />
        <Route
          path='my-course/*'
          element={
            <SuspensedView>
              <MyCoursePage />
            </SuspensedView>
          }
        />
        <Route
          path='my-course-b/*'
          element={
            <SuspensedView>
              <MyCourseBPage />
            </SuspensedView>
          }
        />
        <Route
          path='my-course-exam/*'
          element={
            <SuspensedView>
              <MyCourseExamPage />
            </SuspensedView>
          }
        />
        <Route
          path='my-boardmodule/*'
          element={
            <SuspensedView>
              <MyBoardModulePage />
            </SuspensedView>
          }
        />
        <Route
          path='courseoutcome/*'
          element={
            <SuspensedView>
              <CourseOutcomePage />
            </SuspensedView>
          }
        />
        <Route
          path='exam/*'
          element={
            <SuspensedView>
              <ExamPage />
            </SuspensedView>
          }
        />
        <Route
          path='examquestion/*'
          element={
            <SuspensedView>
              <ExamQuestionPage />
            </SuspensedView>
          }
        />
        <Route
          path='organization/*'
          element={
            <SuspensedView>
              <OrganizationPage />
            </SuspensedView>
          }
        />
        <Route
          path='report/*'
          element={
            <SuspensedView>
              <ReportPage />
            </SuspensedView>
          }
        />
        <Route
          path='reportitem/*'
          element={
            <SuspensedView>
              <ReportItemPage />
            </SuspensedView>
          }
        />
        <Route
          path='reportitemorganization/*'
          element={
            <SuspensedView>
              <ReportItemOrganizationPage />
            </SuspensedView>
          }
        />
        <Route
          path='evidencefile/*'
          element={
            <SuspensedView>
              <EvidenceFilePage />
            </SuspensedView>
          }
        />
        <Route
          path='organizationeducationpurpose/*'
          element={
            <SuspensedView>
              <OrganizationEducationPurposePage />
            </SuspensedView>
          }
        />
        <Route
          path='organizationoutcome/*'
          element={
            <SuspensedView>
              <OrganizationOutcomePage />
            </SuspensedView>
          }
        />
        <Route
          path='outcomepoint/*'
          element={
            <SuspensedView>
              <OutcomePointPage />
            </SuspensedView>
          }
        />
        <Route
          path='questionbank/*'
          element={
            <SuspensedView>
              <QuestionBankPage />
            </SuspensedView>
          }
        />
        <Route
          path='questionitem/*'
          element={
            <SuspensedView>
              <QuestionItemPage />
            </SuspensedView>
          }
        />
        <Route
          path='semester/*'
          element={
            <SuspensedView>
              <SemesterPage />
            </SuspensedView>
          }
        />
        <Route
          path='semestercourse/*'
          element={
            <SuspensedView>
              <SemesterCoursePage />
            </SuspensedView>
          }
        />
        <Route
          path='student/*'
          element={
            <SuspensedView>
              <StudentPage />
            </SuspensedView>
          }
        />
        <Route
          path='my-consultant-students/*'
          element={
            <SuspensedView>
              <MyConsultantStudentsPage />
            </SuspensedView>
          }
        />
        <Route
          path='student-consultant-follow-up/*'
          element={
            <SuspensedView>
              <StudentConsultantFollowUpPage />
            </SuspensedView>
          }
        />
        <Route
          path='studentsemestercourse/*'
          element={
            <SuspensedView>
              <StudentSemesterCoursePage />
            </SuspensedView>
          }
        />
        <Route
          path='survey/*'
          element={
            <SuspensedView>
              <SurveyPage />
            </SuspensedView>
          }
        />
        <Route
          path='surveyapplication/*'
          element={
            <SuspensedView>
              <SurveyApplicationPage />
            </SuspensedView>
          }
        />
        <Route
          path='surveyfrequency/*'
          element={
            <SuspensedView>
              <SurveyFrequencyPage />
            </SuspensedView>
          }
        />
        <Route
          path='surveyquestion/*'
          element={
            <SuspensedView>
              <SurveyQuestionPage />
            </SuspensedView>
          }
        />
        <Route
          path='semesterreport/*'
          element={
            <SuspensedView>
              <SemesterReport />
            </SuspensedView>
          }
        />
        <Route
          path='surveyquestiontype/*'
          element={
            <SuspensedView>
              <SurveyQuestionTypePage />
            </SuspensedView>
          }
        />
        <Route
          path='surveyquestiontypeanswer/*'
          element={
            <SuspensedView>
              <SurveyQuestionTypeAnswerPage />
            </SuspensedView>
          }
        />
        <Route
          path='surveytype/*'
          element={
            <SuspensedView>
              <SurveyTypePage />
            </SuspensedView>
          }
        />
        <Route
          path='courseorganizationoutcomematrix/*'
          element={
            <SuspensedView>
              <CourseOrganizationOutcomeMatrix />
            </SuspensedView>
          }
        />
        <Route
          path='boardmodule/*'
          element={
            <SuspensedView>
              <BoardModulePage />
            </SuspensedView>
          }
        />
        <Route
          path='boardmoduleobjective/*'
          element={
            <SuspensedView>
              <BoardModuleObjectivePage />
            </SuspensedView>
          }
        />
        <Route
          path='boardschedule/*'
          element={
            <SuspensedView>
              <BoardSchedulePage />
            </SuspensedView>
          }
        />
        <Route
          path='boardtimeslotset/*'
          element={
            <SuspensedView>
              <BoardTimeSlotSetPage />
            </SuspensedView>
          }
        />
        <Route
          path='setting/*'
          element={
            <SuspensedView>
              <Setting />
            </SuspensedView>
          }
        />
        <Route
          path='dashboard/*'
          element={
            <SuspensedView>
              <DashboardPage />
            </SuspensedView>
          }
        />
        <Route
          path='teachingassignment/*'
          element={
            <SuspensedView>
              <TeachingAssignmentPage />
            </SuspensedView>
          }
        />
        <Route
          path='assignmentrole/*'
          element={
            <SuspensedView>
              <AssignmentRolePage />
            </SuspensedView>
          }
        />
        <Route
          path='assignmentrule/*'
          element={
            <SuspensedView>
              <AssignmentRulePage />
            </SuspensedView>
          }
        />
        <Route
          path='teachingassignmentslot/*'
          element={
            <SuspensedView>
              <TeachingAssignmentSlotPage />
            </SuspensedView>
          }
        />
        <Route
          path='building/*'
          element={
            <SuspensedView>
              <BuildingPage />
            </SuspensedView>
          }
        />
        <Route
          path='organizationassignment/*'
          element={
            <SuspensedView>
              <OrganizationAssignmentPage />
            </SuspensedView>
          }
        />
        <Route
          path='classroom/*'
          element={
            <SuspensedView>
              <ClassroomPage />
            </SuspensedView>
          }
        />
        <Route
          path='organizationclassroom/*'
          element={
            <SuspensedView>
              <OrganizationClassroomPage />
            </SuspensedView>
          }
        />
        <Route
          path='course-schedule-classroom/*'
          element={
            <SuspensedView>
              <CourseScheduleClassroomPage />
            </SuspensedView>
          }
        />
        <Route
          path='facultyavailability/*'
          element={
            <SuspensedView>
              <FacultyAvailabilityPage />
            </SuspensedView>
          }
        />
        <Route
          path='examseatassignment/*'
          element={
            <SuspensedView>
              <ExamSeatAssignmentPage />
            </SuspensedView>
          }
        />

        <Route
          path='periodloadcounter/*'
          element={
            <SuspensedView>
              <PeriodLoadCounterPage />
            </SuspensedView>
          }
        />

        <Route
          path='additional-teaching'
          element={
            <SuspensedView>
              <AdditionalTeachingPage />
            </SuspensedView>
          }
        />
        <Route
          path='additional-teaching-calculations'
          element={
            <SuspensedView>
              <AdditionalTeachingCalculationPage />
            </SuspensedView>
          }
        />
        <Route
          path='applied-education-visits'
          element={
            <SuspensedView>
              <AppliedEducationVisitPage />
            </SuspensedView>
          }
        />
        <Route
          path='bap-project-applications'
          element={
            <SuspensedView>
              <BapProjectApplicationPage />
            </SuspensedView>
          }
        />
        <Route
          path='bap-project-manage'
          element={
            <SuspensedView>
              <BapProjectManagePage />
            </SuspensedView>
          }
        />
        <Route
          path='bap-project-definitions'
          element={
            <SuspensedView>
              <BapProjectDefinitionPage />
            </SuspensedView>
          }
        />
        <Route
          path='bap-project-reports'
          element={
            <SuspensedView>
              <BapProjectReportsPage />
            </SuspensedView>
          }
        />
        <Route
          path='committee/*'
          element={
            <SuspensedView>
              <CommitteePage view='list' />
            </SuspensedView>
          }
        />
        <Route
          path='committee-meetings/*'
          element={
            <SuspensedView>
              <CommitteePage view='meetings' />
            </SuspensedView>
          }
        />
        <Route
          path='committee-agenda/*'
          element={
            <SuspensedView>
              <CommitteePage view='agenda' />
            </SuspensedView>
          }
        />
        <Route
          path='committee-decisions/*'
          element={
            <SuspensedView>
              <CommitteePage view='decisions' />
            </SuspensedView>
          }
        />
        <Route
          path='committee-minutes/*'
          element={
            <SuspensedView>
              <CommitteePage view='minutes' />
            </SuspensedView>
          }
        />

        {/* Page Not Found */}
        <Route path='*' element={<Navigate to='/error/404' />} />
      </Route>
    </ModuleScopedRoutes>
  )
}

const SuspensedView: FC<WithChildren> = ({children}) => {
  const baseColor = getCSSVariableValue('--bs-primary')
  TopBarProgress.config({
    barColors: {
      '0': baseColor,
    },
    barThickness: 1,
    shadowBlur: 5,
  })
  return (
    <Suspense fallback={<TopBarProgress />}>
      <DisableSidebar>{children}</DisableSidebar>
    </Suspense>
  )
}

export {PrivateRoutes}
